import * as React from "react";
import Api from "./Api";
import "./QuotePage.scss";
import AttachmentDlgCtl from "./AttachmentsDlg";
import CheckCtl from "./input/CheckCtl";
import CustomerCtl from "./CustomerCtl"
import DateCtl from "./input/DateCtl";
import EmailModalCtl from "./EmailModalCtl";
import LabelCtl from "./input/LabelCtl";
import LineItemsCtl from "./LineItemCtl";
import ModalCtl from "./input/ModalCtl";
import PaymentCtl from "./PaymentCtl";
import SelectCtl from "./input/SelectCtl"
import TextCtl from "./input/TextCtl";
import OwnerCtl, { OwnerRoles } from "./input/OwnerCtl";
import TempProvisionCtl from "./TempProvisionCtl";
import Constants from "./Constants"
import { isAccountingPersonnel } from "./Accounting";
import { groupSiteLicenseSubItems } from "./SiteLicense";
import { Guid } from "@testout/testout-commerce/core/Guid";
import { Campus } from "@testout/testout-commerce/models/labsim/Campus";
import { Company } from "@testout/testout-commerce/models/hubspot/Company";
import { ComponentType } from "@testout/testout-commerce/models/commerce/ProductComponent";
import { Deal, LineItem } from "@testout/testout-commerce/models/hubspot/Deal";
import { Customer } from "@testout/testout-commerce/models/commerce/Customer";
import { ItemNumber } from "@testout/testout-commerce/models/commerce/ItemNumber";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { Payment, PaymentTerms } from "@testout/testout-commerce/models/commerce/Payment";
import { Price } from "@testout/testout-commerce/models/commerce/Price";
import { PriceGroupCode } from "@testout/testout-commerce/models/commerce/PriceGroupCode";
import { Product } from "@testout/testout-commerce/models/commerce/Product";
import { ProductProperty, ProductPropertyKeys } from "@testout/testout-commerce/models/commerce/ProductProperty";
import { SalesBase } from "@testout/testout-commerce/models/commerce/SalesBase";
import { SalesItem } from "@testout/testout-commerce/models/commerce/SalesItem";
import Segment from "@testout/testout-commerce/models/Segment";
import { Territory } from "@testout/testout-commerce/models/commerce/Territory";
import { TempProvision } from "@testout/testout-commerce/models/commerce/TempProvision";
import { TempProvisionCampus } from "@testout/testout-commerce/models/commerce/TempProvisionCampus";
import { TempProvisionProduct } from "@testout/testout-commerce/models/commerce/TempProvisionProduct";
import { Quote, QuoteStatus } from "@testout/testout-commerce/models/commerce/Quote";

interface QuotePageProps { }
interface QuotePageState {
    initialized: boolean;
    inProgress: string;
    taxInProgress: boolean;
    errorMessage: string;
    quote: Quote;
    childrenCustomers: Object;
    showModal: boolean;
    showCloseModal: boolean;
    showEmailModal: boolean;
    showProvisionModal: boolean;
    showAttachmentModal: boolean;
    hsCompanyId?: number;
    emailMessage?: string;
    businessAddressCopiedOnce: boolean;
    priceLists?: ListItem[];
    territory?: Territory;
    employees: ListItem[];

    campuses: TempProvisionCampus[];
    expireDate?: Date;
    sendEmail: boolean;
    emailTemplateId: string;
    emailAddresses: string;
    emailSubject: string;
    fromEmployeeNumber: string;
    employeeEmails: ListItem[];
}

export default class QuotePage extends React.PureComponent<QuotePageProps, QuotePageState> {
    constructor(props: QuotePageProps) {
        super(props);
        this.state = {
            initialized: false, inProgress: "", errorMessage: "", taxInProgress: false, quote: new Quote(), childrenCustomers: {}, employees: [],
            showModal: false, showCloseModal: false, showEmailModal: false, showProvisionModal: false, showAttachmentModal: false, businessAddressCopiedOnce: false,
            campuses: new Array<TempProvisionCampus>(), sendEmail: true, emailTemplateId: '', emailAddresses: '', emailSubject: '',
            fromEmployeeNumber: '', employeeEmails: new Array<ListItem>()
        };
    }

    private NEW_QUOTE = ' -- New Quote -- ';

    async componentDidMount() {
        // Parse the URL:
        // quote?dealId=xxxx     - Create a new quote using data from HS deal ID xxxx (customer, line items filled in)
        // quote?companyId=xxxx  - Create a new quote using data from HS company ID xxx (customer filled in)
        // quote/xxxx            - Bring up quote xxxx from the Commerce database
        // quote                 - Bring up a blank quote page

        const query = new URLSearchParams(window.location.search);
        if (query.has("dealId")) {
            let dealId = query.get("dealId");
            try {
                // Load the deal line items into the quote
                let deal = await Api.get<Deal>(`/api/deals/${dealId}`);
                if (deal) {
                    let quote = new Quote();
                    quote.dealId = dealId || undefined;
                    deal.line_items.forEach((lineItem: LineItem) => {
                        var item = new SalesItem();
                        item.itemId = Guid.create();
                        item.productNumber = new ItemNumber(lineItem.hs_sku ?? "");
                        item.quantity = Number(lineItem.quantity ?? "1");
                        item.unitPrice = Number(lineItem.price ?? "0");
                        quote.items.push(item);
                    });

                    groupSiteLicenseSubItems(quote.items);

                    // Set the sequence of the remaining items
                    let sequence = 1;
                    quote.items.filter((item: SalesItem) => item.unitPrice > 0).forEach((item: SalesItem) => item.sequence = sequence++);

                    if (deal.companies.length > 0)
                        this.getCustomerAndLoadPriceList(quote, Number(deal.companies[0]?.hs_object_id), true, deal.companies[0]?.d365_account_number);
                    else
                        this.setState({ errorMessage: 'The HubSpot company for this deal was not found. Please close this window and try again.' });
                } else
                    this.setState({ errorMessage: 'The HubSpot deal was not found. Please close this window and try again.' });
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the deal: ${error.message}` });
            }
        }
        else if (query.has("companyId")) {
            let companyId = query.get("companyId");
            try {
                let company = await Api.get<Company>(`/api/companies/${companyId}`);
                if (company) {
                    let quote = new Quote();
                    this.getCustomerAndLoadPriceList(quote, Number(company.hs_object_id), true, company?.d365_account_number);
                } else
                    this.setState({ errorMessage: 'The HubSpot company was not found. Please close this window and try again.' });
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the HubSpot company ID ${companyId}: ${error.message}` });
            }
        }
        else if (window.location.pathname.lastIndexOf("/") > 1) {
            // Load a saved quote
            let quoteNumber = window.location.pathname.slice(window.location.pathname.lastIndexOf("/") + 1);
            try {
                let quote = new Quote(await Api.get(`/api/quotes/${quoteNumber}`));
                // Fix the UTC time that comes in incorrectly as a local time
                quote.quoteDate = new Date(quote.quoteDate.toLocaleString() + " GMT");
                if (quote.quoteNumber !== quoteNumber) {
                    this.setState({ errorMessage: `Unable to find quote ${quoteNumber}`, initialized: true });
                    return;
                }
                if (quote.shippingCustomer) {
                    let customerNumber = quote.shippingCustomer.isStudent
                        ? (quote.shippingCustomer.parentCustomerNumber ?? "")
                        : quote.shippingCustomer.customerNumber;
                    let hsCompanyId = await Api.get(`/api/companies/hsCompanyId/${customerNumber}`);
                    this.setState({ hsCompanyId: hsCompanyId });
                }
                // Open the drop down to show bundle sub-items if they are there
                for (let item of quote.items) {
                    let parentId = item.parentItemId;
                    if (parentId) {
                        let parents = quote.items.filter((item: SalesItem) => item.itemId === parentId);
                        if (parents?.length === 1)
                            parents[0].showBundle = true;
                    }
                }

                this.setCustomerTerritory(quote);
                quote = await this.loadChildrenAndPriceList(quote, true);
                this.setState({ quote: new Quote(quote) });
            } catch (error: any) {
                this.setState({ errorMessage: `Error while loading the quote: ${error.message}` });
            }
        }

        // Save the employee list for lookups
        let employees = await Api.get<ListItem[]>('/api/employees/list-items');
        this.setState({ employees: employees });

        this.setState({ initialized: true });
    }

    async getCustomerAndLoadPriceList(quote: Quote, hsCompanyId: number, checkOverride: boolean, customerNumber?: string) {
        try {
            // Load the customer from the database if it is new to this quote
            let customer = quote.shippingCustomer;
            if ((customerNumber ?? "") !== "") {
                customer = await Api.get<Customer>(`/api/customers/${customerNumber}`);
                if (!customer)
                    this.setState({ errorMessage: `Unable to find customer number ${customerNumber} in the Commerce database` });
                // Clear unneeded field
                customer.parentCustomerNumber = "";
            }

            quote = await this.setQuoteCustomerAndLoadPriceList(customer, quote, hsCompanyId, checkOverride);
            this.setState({ quote: quote });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the customer information: ${error.message}` });
        }
    }

    async setQuoteCustomerAndLoadPriceList(customer: Customer, quote: Quote, hsCompanyId: number = 0, checkOverride: boolean): Promise<Quote> {
        quote.shippingCustomer = customer;
        quote.priceGroupId = customer.priceGroupId;
        quote.priceId = customer.priceId;
        quote.paymentTerms = customer.paymentTerms;
        quote.useAutomatedFulfillment = customer.useAutomatedFulfillment;
        if (!quote.salespersonCode)
            quote.salespersonCode = customer.salespersonCode;
        // If the customer is assigned a different billing customer, then load it
        if (customer.billingCustomerNumber && customer.billingCustomerNumber !== customer.customerNumber) {
            quote.billingSameAsShipping = false;
            try {
                quote.billingCustomer = await Api.get<Customer>(`/api/customers/${customer.billingCustomerNumber}`);
                if (!quote.billingCustomer)
                    this.setState({ errorMessage: `Unable to find billing customer number ${customer.billingCustomerNumber} in the Commerce database` });
                // Clear unneeded fields
                quote.billingCustomer.billingCustomerNumber = "";
                quote.billingCustomer.parentCustomerNumber = "";
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the billing customer ${customer.billingCustomerNumber}: ${error.message}` });
            }
        } else {
            quote.billingSameAsShipping = true;
        }
        customer.billingCustomerNumber = "";     // Clear unneeded field
        if (hsCompanyId !== 0)
            this.setState({ hsCompanyId: hsCompanyId });

        await this.setCustomerTerritory(quote);
        return await this.loadChildrenAndPriceList(quote, checkOverride);
    }

    async setCustomerTerritory(quote: Quote) {
        let territory: Territory;
        if (quote.territoryCode)
            territory = await Api.getObject(Territory, `/api/territories/${quote.territoryCode}`);
        else if (quote.shippingCustomer.territoryCode)
            territory = await Api.getObject(Territory, `/api/territories/${quote.shippingCustomer.territoryCode}`);
        else {
            let customerNumber = quote.shippingCustomer.isStudent
                ? (quote.shippingCustomer.parentCustomerNumber ?? "")
                : quote.shippingCustomer.customerNumber;
            let customer = await Api.get<Customer>(`/api/customers/${customerNumber}`);
            if (!customer)
                return;
            if (customer.territoryCode)
                territory = await Api.getObject(Territory, `/api/territories/${customer.territoryCode}`);
            else
                territory = await Api.getObject(Territory, `/api/customers/territories?name=${encodeURIComponent(customer.name)}&segment=${customer.segment}&province=${customer.address.postal.stateOrProvince}&country=${customer.address.postal.countryCode}`);
        }
        if (territory) {
            // Default to the AE as the sales person unless the CSM or the MCS have been selected
            if (!(quote.salespersonCode === territory.roles[OwnerRoles.CSM] || quote.salespersonCode === territory.roles[OwnerRoles.MCS]))
                quote.salespersonCode = territory.roles[OwnerRoles.AE];
            quote.territoryCode = territory.territoryCode;
        }
        this.setState({ territory: territory });
    }

    async loadChildrenAndPriceList(quote: Quote, checkOverride: boolean): Promise<Quote> {
        await this.loadChildrenCustomersList(quote.shippingCustomer);
        quote = await this.loadPriceList(quote);
        if (checkOverride) {
            // Set the override to true if the price is not the default (unless it is a price-locked product)
            quote.items.filter(item => item._unitPrice !== item.pricing?.unitPrice &&
                !quote.pricing?.priceList?.products.find((p: Product) => p.productNumber === item.productNumber?.toString())?.properties?.some((pp: ProductProperty) => pp.key === ProductPropertyKeys.PriceLocked)
            ).forEach(item => { item.overridePricing = true });
            // If the billing shipping is already not same as delivery, don't copy it again
            if (!quote.billingSameAsShipping)
                this.setState({ businessAddressCopiedOnce: true });
        }
        return quote;
    }

    async loadPriceList(quote: Quote): Promise<Quote> {
        try {
            quote.pricing = await Api.getObject(PriceGroupCode, `/api/prices/pricegroupcodebyid?priceGroupId=${quote?.priceGroupId ?? 0}&priceId=${quote?.priceId ?? 15}`);

            // Re-assign unit codes from this price list to any existing sales items (if we can find matches)
            quote.items.forEach((item: SalesItem) => { item.unitCode = quote.pricing?.priceList?.prices.find((p: Price) => p.productNumber === item.productNumber.toString())?.unitCode ?? item.unitCode });

            // Load the price lists possible for this price group ID
            this.setState({ priceLists: await Api.get<ListItem[]>(`/api/prices/grouppricelists/${quote?.priceGroupId ?? 0}`) });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the price list: ${error.message}` });
        }
        return quote;
    }

    async loadChildrenCustomersList(customer: Customer) {
        let customerNumber = customer.isStudent ? (customer.parentCustomerNumber ?? "") : customer.customerNumber;
        if (customerNumber === "")
            return;
        let errorMessage = "";
        try {
            let childSchools = await Api.get(`/api/customers/${customerNumber}/children`);
            if (Object.entries(childSchools).length > 0) {
                Object.keys(childSchools).forEach(key => {   // For some reason the first 4 characters of the customer number are lower case. Force it to upper case.
                    var upper = key.toUpperCase();
                    if (upper !== key) {
                        childSchools[key.toUpperCase()] = childSchools[key];
                        delete childSchools[key];
                    }
                });
                this.setState({ childrenCustomers: { [Constants.MAIN_SCHOOL]: [customer.name], ...childSchools } });
            } else {
                // Clear any prior values
                this.setState({ childrenCustomers: {} });
            }
        }
        catch (error: any) {
            errorMessage = `Error while loading the sub-schools: ${error.message} `;
        }

        // Add up a warning error message if the customer's credit is on hold
        if (customer.creditOnHold)
            errorMessage += "WARNING! This customer's credit is ON HOLD";

        this.setState({ errorMessage: errorMessage });
    }

    /* // Save purchase quote changes to the quote
    onPurchaseOrderNumberChange = (value: string) => {
        let quote = new Quote(this.state.quote);
        quote.purchaseOrderNumber = value;
        this.setState({ quote: quote });
    } */

    // Save automated fulfillment checkbox changes to the quote
    onAutomatedFulfillmentChange = (value: boolean) => {
        let quote = new Quote(this.state.quote);
        quote.useAutomatedFulfillment = value;
        this.setState({ quote: quote });
    }

    // Save quote date changes to the quote
    onQuoteDateChange = (value?: Date) => {
        let quote = new Quote(this.state.quote);
        quote.quoteDate = value ?? new Date();
        this.setState({ quote: quote });
    }

    // Save expiration date changes to the quote
    onExpirationDateChange = (value?: Date) => {
        let quote = new Quote(this.state.quote);
        quote.expirationDate = value ?? new Date();
        this.setState({ quote: quote });
    }

    // Save any changes to the shipping customer info, if the customer has changed, reload the price list
    onShippingCustomerChange = async (customer: Customer) => {
        let quote = new Quote(this.state.quote);
        // Flag if the state/province or zipcode has changed
        let recalculateTaxes = (quote.shippingCustomer?.address.postal.stateOrProvince !== customer.address.postal.stateOrProvince ||
            quote.shippingCustomer?.address.postal.postalCode !== customer.address.postal.postalCode);
        quote.billingCustomer = quote.billingSameAsShipping ? customer : quote.billingCustomer;

        // If a new customer was selected, load the school info and any sub-schools for this customer
        if (quote.shippingCustomer.customerNumber !== customer.customerNumber) {
            // Force the sales person and territory to be re-loaded from the customer's info
            quote.salespersonCode = undefined;
            quote.territoryCode = undefined;
            for (let item of quote.items)  // Clear any prior sub-schools
                item.customerNumber = Constants.MAIN_SCHOOL;
            quote = await this.setQuoteCustomerAndLoadPriceList(customer, quote, 0, false);
        }
        else {
            quote.shippingCustomer = customer;
        }

        // Recalculate the taxes if the state/province or zipcode has changed
        if (recalculateTaxes && quote.items.length > 0)
            this.checkTheTaxes(quote);
        this.setState({ quote: quote });
    }

    // Save any changes to the billing same as shipping check box
    onBillingSameAsShippingChange = (value: boolean) => {
        let quote = new Quote(this.state.quote);
        // If this is the first time this checkbox has been turned off, copy the data from shipping to billing
        if (this.state.businessAddressCopiedOnce === false && value === false) {
            quote.billingCustomer = JSON.parse(JSON.stringify(quote.shippingCustomer));
            this.setState({ businessAddressCopiedOnce: true });
        }
        quote.billingSameAsShipping = value;
        this.setState({ quote: quote });
    }

    // Save any changes to the billing customer info
    onBillingCustomerChange = (customer: Customer) => {
        let quote = new Quote(this.state.quote);
        quote.billingCustomer = customer;
        this.setState({ quote: quote });
    }

    onPriceIdChange = async (value: string) => {
        // Load the newly-selected price list into the current quote
        const priceId = parseInt(value);
        let quote = new Quote(this.state.quote);
        quote.priceId = priceId;
        quote = await this.loadPriceList(quote);

        // If the quote has items, re-check the taxes since the prices of the quote's items may have changed
        if (quote.items.length > 0)
            this.checkTheTaxes(quote);
        this.setState({ quote: quote });
    }

    // Save any changes to the quote payment info
    onPaymentChange = (paymentTerms: string, payment: Payment | undefined) => {
        let quote = new Quote(this.state.quote);
        quote.paymentTerms = paymentTerms;
        quote.payments = (payment) ? [payment] : new Array<Payment>();
        this.setState({ quote: quote });
    }

    // Save any changes to the quote comments
    onCommentsChange = (value: string) => {
        let quote = new Quote(this.state.quote);
        quote.comments = value;
        this.setState({ quote: quote });
    }

    onSalespersonChange = (value: string) => {
        let quote = new Quote(this.state.quote);
        quote.salespersonCode = value;
        this.setState({ quote: quote });
    }

    // The selected customer has changed, load the newly-selected customer info
    onItemCustomerSelect = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        try {
            this.setState({ errorMessage: "" });        // Clear any prior errors
            return exact
                ? [await Api.get<ListItem>(`/api/customers/${value}/list-item`)]
                : await Api.get<ListItem[]>(`/api/customers/list-items?search=${encodeURIComponent(value)}`);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the customer information: ${error.message}` });
            return [{ name: "", value: "" }];
        }
    }

    setStateQuote = (salesBase: SalesBase) => {
        let quote = new Quote({
            ...salesBase,
            quoteId: this.state.quote.quoteId, status: this.state.quote.status,
            quoteNumber: this.state.quote.quoteNumber, quoteDate: this.state.quote.quoteDate, expirationDate: this.state.quote.expirationDate,
            fromEmployeeNumber: "", CCemailAddresses: ""
        });

        // Manually copy calculated fields
        quote.currencyCode = salesBase.currencyCode;
        quote.priceCode = salesBase.priceCode;
        quote.priceGroupId = salesBase.priceGroupId;
        quote.priceId = salesBase.priceId;
        quote.billingSameAsShipping = salesBase.billingSameAsShipping;
        quote.billingCustomer = salesBase.billingCustomer;
        quote.shippingCustomer = salesBase.shippingCustomer;

        this.setState({ quote: quote });
    }

    showError = (message: string) => this.setState({ errorMessage: message });

    checkingTaxes = (inProgress: boolean) => this.setState({ taxInProgress: inProgress });

    private _taxTimer: number | undefined = undefined;
    checkTheTaxes = async (quote: Quote) => {
        if (this._taxTimer)
            window.clearTimeout(this._taxTimer);
        this._taxTimer = window.setTimeout(async () => {
            this._taxTimer = undefined;
            this.checkingTaxes(true);
            let salesBase = quote;
            try {
                // Use Commerce Server to contact Avalara for the tax amount(s) of the current order
                let taxedQuote = await Api.post<SalesBase>("/api/orders/gettaxes", salesBase);

                // Copy the tax amounts we got from Avalara
                salesBase.taxSchedule = taxedQuote.taxSchedule;
                salesBase.freightTaxAmount = taxedQuote.freightTaxAmount;
                for (var item of salesBase.items) {
                    let itemId = item.itemId.toString();
                    let taxedItem = taxedQuote.items.filter((taxItem: SalesItem) => taxItem.itemId.toString() === itemId);
                    if (taxedItem !== undefined && taxedItem[0] !== undefined)
                        item.taxAmount = taxedItem[0].taxAmount;
                }
                salesBase.shippingCustomer.address.postal.postalCode = this.state.quote.shippingCustomer.address.postal.postalCode;    // Get any user input changes while calculating taxes
                this.setStateQuote(salesBase);
                this.checkingTaxes(false);
            }
            catch (error: any) {
                this.showError(`Taxes were not calculated correctly: ${error.message}`);
                this.checkingTaxes(false);
            }
        }, 500);
    }

    // Save the current quote to the Commerce Server
    saveQuote = async () => {
        let quote = this.cleanUpQuote();

        // Save the quote
        try {
            this.setState({ errorMessage: "", inProgress: "Saving the quote..." });        // Clear any prior errors and set that we are saving
            let quoteNumber = await Api.post<string>("/api/quotes/savedraft", quote);
            quote.quoteNumber = quoteNumber;
            quote.status = QuoteStatus.Draft;
            this.setState({ quote: quote, inProgress: ""});
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while saving the draft sales quote: ${error.message}`, inProgress: "" });
            return;
        }
    }

    // Bring up the order page using the contents of this quote
    createOrder = () => {
        window.location.href = `/salesorder?quoteNumber=${this.state.quote.quoteNumber}`;
    }

    private cleanUpQuote = (): Quote => {
        // Set any required fields that are blank
        let quote = new Quote(this.state.quote);
        if (quote.shippingMethods?.length !== 0)
            quote.shippingMethods = ["Internet Delivery"];
        if (quote.quoteId.value === Guid.EMPTY)
            quote.quoteId = Guid.create();
        if (!quote.territoryCode || quote.territoryCode === "")
            quote.territoryCode = quote.shippingCustomer.territoryCode;

        // Fix the default customer number to be un-set
        quote.items.filter(item => item.customerNumber === Constants.MAIN_SCHOOL).forEach(item => item.customerNumber = undefined);

        return quote;
    }

    // Activate the current quote through the Commerce Server
    activateQuote = async () => {
        let quote = this.cleanUpQuote();
        // Check the quote for missing values
        if ((quote.shippingCustomer?.address?.email ?? "") === "") {
            this.setState({ errorMessage: 'Quote does not have a shipping email address' });
            return;
        }
        if ((quote.billingCustomer?.address?.email ?? "") === "") {
            this.setState({ errorMessage: 'Quote does not have a billing email address' });
            return;
        }

        // Submit the quote
        try {
            this.setState({ errorMessage: "", inProgress: "Activating quote..." });        // Clear any prior errors and set that we are activating
            await Api.post<string>("/api/quotes/submit", quote);

            // Redisplay the quote
            window.location.href = `/quote/${this.state.quote.quoteNumber}`;
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while submitting the sales quote: ${error.message}`, inProgress: "" });
        }
    }

    // Revise the current quote through the Commerce Server
    reviseQuote = async () => {
        let quote = this.cleanUpQuote();

        // Revise the quote
        try {
            this.setState({ errorMessage: "", inProgress: "Revising quote..." });        // Clear any prior errors and set that we are revising
            await Api.post<string>("/api/quotes/revise", quote);

            // Redisplay the quote
            window.location.reload();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while revising the sales quote: ${error.message}`, inProgress: "" });
        }
    }

    // Check if this quote is ready to submit
    readyToSubmit = (): boolean => {
        if (isAccountingPersonnel())
            return false;
        if (this.state.inProgress !== "" || this.state.taxInProgress)  // If we are currently saving or calculating taxes, don't allow a (second) click
            return false;
        let quote = this.state.quote;
        if (quote.status !== QuoteStatus.Draft)                        // If this is a previously submitted quote, don't allow a re-submit
            return false;
        if (quote.shippingCustomer === undefined)                       // Customer must be selected
            return false;
        if (quote.items.length === 0)                                  // At least 1 sales item must be added
            return false;
        if (quote.shippingCustomer?.creditOnHold)                      // Customer cannot have their credit on hold
            return false;
        if (quote.quoteNumber === "")                                  // Quote must have a quote number because it has been saved
            return false;
        return true;
    }

    // Get the list of employee emails to show in the two popups
    getEmployeeEmailList = async (territoryCode?: string): Promise<ListItem[]> => {
        // Put the AE, CSM, etc for the territory first on the dropdown list
        let orderedList = new Array<ListItem>();
        if (territoryCode && territoryCode !== '') {
            let territory = await Api.get<Territory>(`/api/territories/${territoryCode}`);
            if (territory?.roles) {
                for (let role in territory.roles) {
                    let employeeNumber = territory.roles[role].toString();
                    for (let i = 0; i < this.state.employees.length; i++) {
                        if (this.state.employees[i].value === employeeNumber) {
                            if (!orderedList.includes(this.state.employees[i]))
                                orderedList.push(this.state.employees[i]);
                            break;
                        }
                    }
                }
            }
        }

        // Put the remaining employees on the ordered list
        for (let employee of this.state.employees)
            if (!orderedList.includes(employee))
                orderedList.push(employee);

        return orderedList;
    }

    // Cancel any modal dialogs by hiding any that are currently displaying
    cancelModal = () => { this.setState({ showModal: false, showCloseModal: false, showEmailModal: false, showProvisionModal: false, showAttachmentModal: false }); }

    // SEND QUOTE EMAIL TO CUSTOMER

    // Show the modal to send the quote to a customer
    showSendToCustomerModal = async () => {
        this.setState({ errorMessage: '' });
        if (this.state.quote.expirationDate < new Date()) {
            this.setState({ errorMessage: 'Quote has expired. Cannot send an expired quote to a customer.' });
            return;
        }

        try {
            let quote = this.state.quote;

            // Check that we have a destination email address to send to
            if ((this.state.quote?.billingCustomer.address?.email ?? "") === "") {
                this.setState({ errorMessage: 'No email address(es) were entered for the billing customer. Please revise the quote and add one.' });
                return;
            }

            let employeeEmails = await this.getEmployeeEmailList(quote.territoryCode);

            // If the email that will go with the quote is blank, go get it
            let fromEmployeeNumber = employeeEmails[0].value ?? "";
            if (!this.state.quote.primaryDocument) {
                let tmpQuote = await Api.get(`/api/quotes/email/${quote.quoteNumber}?fromEmployeeNumber=${fromEmployeeNumber}`);
                if (tmpQuote)
                    quote.documents = tmpQuote.documents;
                else {
                    this.setState({ errorMessage: 'Unable to get the quote email template. Please submit a service desk ticket.' });
                    return;
                }
            }
            this.setState({
                showEmailModal: true,
                employeeEmails: employeeEmails,
                fromEmployeeNumber: fromEmployeeNumber,
                emailAddresses: "",
                emailMessage: atob(quote.primaryDocument?.base64Content ?? ""),
                emailSubject: quote.primaryDocument?.name ?? ""
            });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while getting the quote email: ${error.message}`, showEmailModal: false });
        }
    }

    // Values were changed in the Send quote dialog, remember them in the state variables
    emailChange = (fromEmployeeNumber: string, CCaddresses: string, emailSubject: string) => {
        this.setState({ fromEmployeeNumber: fromEmployeeNumber, emailAddresses: CCaddresses, emailSubject: emailSubject });
    }

    // Do the requested action of Send Quote to Customer
    submitEmailModal = async (text: string) => {
        try {
            this.setState({ errorMessage: "", showEmailModal: false, inProgress: "Sending quote to the customer...." });    // Clear any prior errors and set that we are sending the quote

            // First, put the email message from the EmailModalCtl into the quote
            let quote = this.state.quote;

            // Get the original email document if we don't have it yet
            if (!quote.primaryDocument) {
                let tmpQuote = await Api.get<Quote>(`/api/quotes/email/${this.state.quote.quoteNumber}`);
                quote.documents = tmpQuote.documents;
            }

            // The text from EmailModalCtl is only the body for the email (the editor strips the header out),
            //   so insert it into the email body in the primary document
            let doc = atob(quote.primaryDocument?.base64Content ?? "");
            if (doc !== undefined && doc.includes("<body>"))
                doc = doc.substr(0, doc.indexOf("<body>") + 6) + text + doc.substr(doc.indexOf("</body>"));

            // Send the quote to the customer with the modified email message and email subject
            if (quote.primaryDocument) {
                quote.primaryDocument.base64Content = btoa(doc);
                quote.primaryDocument.name = this.state.emailSubject;
            }

            // Make sure that all the quote data is filled out
            quote.fromEmployeeNumber = this.state.fromEmployeeNumber;
            quote.CCemailAddresses = this.state.emailAddresses.replaceAll(';', ','); // Change any semi-colon separators to commas in the list of email addresses
            quote.items.forEach((item: SalesItem) => item.description = item.pricing?.product?.title ?? "");
            if (quote.shippingMethods?.length !== 0)
                quote.shippingMethods = ["Internet Delivery"];

            await Api.post<string>("/api/quotes/process", quote);
            this.setState({ quote: quote });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while sending the sales quote: ${error.message}` });
        }

        this.setState({ inProgress: "" });
    }

    // DELETE THE QUOTE

    // Do the requested action of deleting the quote
    deleteQuote = async () => {
        // Clear any prior errors and set that we are deleting the quote
        this.setState({ errorMessage: "", showModal: false, inProgress: "Deleting the quote..." });
        try {
            // Delete the draft quote
            await Api.delete(`/api/quotes/${this.state.quote.quoteNumber}`);
            this.closeTheWindow();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while deleting the quote: ${error.message}` });
        }
        this.setState({ inProgress: "" });
    }

    // MANUALLY CLOSE THE QUOTE

    // Do the requested action of manually closing the quote
    manuallyCloseQuote = async () => {
        // Clear any prior errors and set that we are closing the quote
        this.setState({ errorMessage: "", showCloseModal: false, inProgress: "Manually closing the quote..." });
        try {
            // Close the quote
            await Api.put<object>(`/api/quotes/close/${this.state.quote.quoteNumber}`, null);

            //// Redisplay the closed quote
            window.location.href = `/quote/${this.state.quote.quoteNumber}`;
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while manually closing the quote: ${error.message}` });
        }
        this.setState({ inProgress: "" });
    }

    // TEMPORARY PROVISIONING

    // Get the labsim campus for a customer number
    campusId?: Guid;
    async getLabSimCampus(customerNumber: string): Promise<TempProvisionCampus> {
        this.campusId = undefined;
        let newCampus = true;
        let newCampusName = "";
        let customerName = "";
        let company = await Api.get<Company>(`/api/companies/labsimid/${customerNumber}`);
        if (company) {
            customerName = company?.name ?? "";
            newCampusName = company.name || "";
            if (company.labsim6_companyprofileid) {
                let campus = await Api.get<Campus>(`/api/campuses/${company.labsim6_companyprofileid}`);
                if (campus?.campusId) {
                    this.campusId = new Guid(campus.campusId);
                    newCampusName = campus.campusName;
                    newCampus = false;
                }
            }
        }

        let campus = new TempProvisionCampus({
            customerNumber: customerNumber,
            labSimCampusId: this.campusId,
            createNewCampus: newCampus,
            newCampusName: newCampusName
        });
        campus.customerName = customerName;
        return campus;
    }

    // Show the modal that allows temp licenses to be provisioned
    async showProvisionLicensesModal() {
        this.setState({ errorMessage: "", inProgress: "Getting temp provisioning info from LabSim...." });      // Clear prior errors and tell the user what is happening
        
        let quote = this.state.quote;

        let expireDate = new Date((quote.quoteDate.valueOf() > Date.now() ? quote.quoteDate.valueOf() : Date.now()) + 90 * 24 * 60 * 60 * 1000);
        // BIHD-1098 - For K12, please change the default temporary license expiration date to September 30th.  On September 18th, move this out to October 31st.
        let isSecondaryEd = new Segment(quote.shippingCustomer.segment ?? "").parentSegment?.code === Segment.secondaryEd.code;
        if (isSecondaryEd)
            expireDate = new Date(2024, 8, 30);     // 8 = September, 9 = October

        // Get a list of unique customer numbers on the quote line items
        let campuses = new Array<TempProvisionCampus>();
        for (let item of quote.items) {
            // By default, use the main shipping customer number
            let customerNumber = quote.shippingCustomer.isStudent
                ? (quote.shippingCustomer.parentCustomerNumber ?? "")
                : quote.shippingCustomer.customerNumber;
            // If there is a customer number on the line item, use it instead
            if (item.customerNumber)
                customerNumber = item.customerNumber;
            // If this customer number is not already in the list, add it
            if (campuses.filter(c => c.customerNumber === customerNumber).length === 0)
                campuses.push(await this.getLabSimCampus(customerNumber));
        }

        let employeeEmails = await this.getEmployeeEmailList(this.state.quote.territoryCode);

        this.setState({
            expireDate: expireDate,
            campuses: campuses,
            sendEmail: true,
            emailTemplateId: 'TempProvisionLicenses',
            emailAddresses: this.state.quote.shippingCustomer?.address?.email ?? "",
            fromEmployeeNumber: employeeEmails[0]?.value ?? "",
            employeeEmails: employeeEmails,
            showProvisionModal: true,
            inProgress: ""
        });
    }

    // Values were changed in the temp provision dialog, remember them in the state variables
    onTempProvisionChange = (campuses: TempProvisionCampus[], sendEmail: boolean, emailTemplateId: string,
        emailAddresses: string, fromEmployeeNumber: string, expireDate?: Date) => {

        // We have to clone the campuses array so that setState will recognize that it has changed
        let newCampuses = new Array<TempProvisionCampus>();
        for (let campus of campuses) {
            newCampuses.push(campus)
        }

        this.setState({
            expireDate: expireDate, campuses: newCampuses, sendEmail: sendEmail, emailTemplateId: emailTemplateId,
            emailAddresses: emailAddresses, fromEmployeeNumber: fromEmployeeNumber
        });
    }

    // Do the requested action of provisioning temp licenses and sending an email to the customer(s)
    submitTempModal = async () => {
        try {
            if (!this.state.campuses) {
                this.setState({ errorMessage: 'Campuses information is missing. Please submit a service desk ticket.' });
                return;
            }
            for (let campus of this.state.campuses) {
                if (!campus.labSimCampusId && !campus.createNewCampus) {
                    this.setState({ errorMessage: 'Unable to find the LabSim Campus ID for ' + campus.customerName });
                    return;
                }
            }
            if (!this.state.expireDate || this.state.expireDate < new Date()) {
                this.setState({ errorMessage: 'Invalid expiration date' });
                return;
            }
            this.setState({ errorMessage: "", showProvisionModal: false, inProgress: "Provisioning temp licenses...." });        // Clear any prior errors and set that we are provisioning

            // Create the temp provision request
            let customerNumber = this.state.quote.shippingCustomer.isStudent
                ? (this.state.quote.shippingCustomer.parentCustomerNumber ?? "")
                : this.state.quote.shippingCustomer.customerNumber;
            let request = new TempProvision();
            request.quoteNumber = this.state.quote.quoteNumber;
            request.licenseExpirationDate = this.state.expireDate;
            request.campuses = this.state.campuses;
            request.products = new Array<TempProvisionProduct>();
            for (let item of this.state.quote.items) {
                let product = new TempProvisionProduct();
                product.name = item.title;
                product.quantity = item.quantity;
                product.customerNumber = item.customerNumber || customerNumber;
                product.courseIds = new Array<number>();
                if (item.pricing?.product?.courseId)
                    product.courseIds.push(item.pricing.product.courseId);  // Add the main product course ID (if it has one)
                let productNumber = item.productNumber?.toString();
                let components = this.state.quote.pricing?.priceList?.products.find(p => p.productNumber === productNumber)
                        ?.components.filter(c => c.componentType === ComponentType.Required);
                if (components)
                    for (let component of components) {
                        if (component.product?.courseId)    // Add any sub-component course IDs
                            product.courseIds.push(component.product.courseId);
                    }
                if (product.courseIds.length === 0)
                    continue;       // No need to provision something that doesn't have any course IDs
                request.products.push(product);
            }
            request.sendEmailToCustomer = this.state.sendEmail;
            request.fromEmployeeNumber = this.state.fromEmployeeNumber;
            request.emailAddresses = this.state.emailAddresses.replace(';', ',');  // Change any semi-colon separators to commas in the list of email addresses
            request.emailTemplateId = this.state.emailTemplateId;
            request.emailCulture = this.state.quote.culture ?? "en-US";

            // Send it to the Commerce Server
            await Api.post<string>("/api/quotes/provision", request);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while doing temp provisioning: ${error.message}` });
        }
        this.setState({ inProgress: "" });
    }

    closeTheWindow = () => {
        window.parent.postMessage(JSON.stringify({ action: "CANCEL" }), "*");
    }

    render() {
        if (!this.state.initialized)
            return <div className="page QuotePage loading"><div className="spinner"></div></div>;

        let quote = this.state.quote;
        return <div className="page QuotePage">
            {this.state.showModal &&
                <ModalCtl headerText="Delete this draft quote?" onSubmit={this.deleteQuote} onCancel={this.cancelModal}
                          description="To delete this draft quote, click the Delete button. This action is permananent."
                          saveInProgress={this.state.inProgress !== ""} submitButton="Delete" />
            }
            {this.state.showCloseModal &&
                <ModalCtl headerText="Manually close this quote?" onSubmit={this.manuallyCloseQuote} onCancel={this.cancelModal}
                    description="If you need to close this quote, click the Manually Close button."
                    saveInProgress={this.state.inProgress !== ""} submitButton="Manually Close" />
            }
            {this.state.showEmailModal &&
                <EmailModalCtl quoteNumber={quote.quoteNumber} emailMessage={this.state.emailMessage ?? ""} subject={this.state.emailSubject}
                               fromEmployeeNumber={this.state.fromEmployeeNumber} employeeEmails={this.state.employeeEmails}
                               toAddresses={quote.shippingCustomer?.address?.email ?? ""} CCaddresses={this.state.emailAddresses}
                               onChange={this.emailChange} onSubmit={this.submitEmailModal} onCancel={this.cancelModal} />
            }
            {this.state.showProvisionModal &&
                <TempProvisionCtl expireDate={this.state.expireDate} campuses={this.state.campuses} sendEmail={this.state.sendEmail}
                                  fromEmployeeNumber={this.state.fromEmployeeNumber} emailAddresses={this.state.emailAddresses}
                                  emailTemplateId={this.state.emailTemplateId} employeeEmails={this.state.employeeEmails}
                                  onChange={this.onTempProvisionChange} onSubmit={this.submitTempModal} onCancel={this.cancelModal}
                                  errorMessage={this.state.errorMessage} />
            }
            {this.state.showAttachmentModal && <AttachmentDlgCtl onClose={this.cancelModal} quoteNumber={quote.quoteNumber} />}
            <header>
                <div className="title">
                    <h3>{quote.quoteNumber === "" ? this.NEW_QUOTE : quote.quoteNumber}</h3>
                    <h6>{QuoteStatus[quote.status]}</h6>
                </div>
                <div className="actions">
                    {quote.status === QuoteStatus.Active
                        ? <button className="tertiary" disabled={this.state.inProgress !== "" || isAccountingPersonnel()} onClick={() => this.reviseQuote()}>Revise</button>
                        : <button className="tertiary" disabled={!this.readyToSubmit()} onClick={() => this.activateQuote()}>Activate</button>
                        }
                    <button className="tertiary" disabled={quote.status !== QuoteStatus.Active || this.state.inProgress !== "" || isAccountingPersonnel()}
                            onClick={() => this.showSendToCustomerModal()}>Send to Customer</button>
                    <button className="tertiary" disabled={quote.status !== QuoteStatus.Active || this.state.inProgress !== "" || isAccountingPersonnel()}
                            onClick={() => this.showProvisionLicensesModal()}>Temp Licenses</button>
                    <button className="tertiary" disabled={quote.status !== QuoteStatus.Active || this.state.inProgress !== "" || isAccountingPersonnel()}
                            onClick={() => this.createOrder()}>Create Order</button>
                </div>
                <OwnerCtl value={quote.salespersonCode} territory={this.state.territory} employees={this.state.employees} onChange={this.onSalespersonChange} />
                {this.state.errorMessage !== "" && <p className="error">{this.state.errorMessage}</p>}
            </header>

            <main>
                <section className="common">
                    {/*<LabelCtl label="PO Number"><TextCtl value={quote.purchaseOrderNumber} onChange={this.onPurchaseOrderNumberChange}/></LabelCtl>*/}
                    <LabelCtl label="Quote Date">
                        <DateCtl value={quote.quoteDate} onChange={this.onQuoteDateChange}
                                 disabled={quote.status === QuoteStatus.Active || this.state.inProgress !== ""}/>
                    </LabelCtl>
                    <LabelCtl label="Quote Expiration Date">
                        <DateCtl value={quote.expirationDate} onChange={this.onExpirationDateChange}
                                 disabled={quote.status === QuoteStatus.Active || this.state.inProgress !== ""}/>
                    </LabelCtl>
                    <CheckCtl className="manual" label="Order will be fulfilled automatically" value={quote.useAutomatedFulfillment}
                              onChange={this.onAutomatedFulfillmentChange} disabled={quote.status !== QuoteStatus.Draft}/>
                </section>

                <section className="delivery">
                    <h3>Delivery</h3>
                    <CustomerCtl customer={quote.shippingCustomer ?? new Customer({ address: { addressCode: "SHIPPING" } })}
                                 onChange={this.onShippingCustomerChange} hsCompanyId={this.state.hsCompanyId}
                                 disabled={quote.status !== QuoteStatus.Draft} isRequired={true} onSetErrorMessage={this.showError} />
                    <label><span>Pricing</span><span className="required">&nbsp;*</span>
                        <SelectCtl value={(quote.priceId ?? "").toString()} onChange={this.onPriceIdChange} hideItemValue={false}
                                   errorMessage={quote.shippingCustomer === undefined ? "Please select a customer" : ""}
                                   items={this.state.priceLists ?? new Array<ListItem>()} disabled={quote.status !== QuoteStatus.Draft}/>
                    </label>
                </section>

                <section className="billing">
                    <CheckCtl className="check-right" label="Same as Delivery" value={quote.billingSameAsShipping}
                              onChange={this.onBillingSameAsShippingChange} disabled={quote.status !== QuoteStatus.Draft}/>
                    <h3>Billing</h3>
                    <CustomerCtl customer={quote.billingCustomer ?? new Customer({ address: { addressCode: "BILLING" } })}
                                 onChange={this.onBillingCustomerChange} hsCompanyId={this.state.hsCompanyId}
                                 disabled={quote.billingSameAsShipping || quote.status !== QuoteStatus.Draft}/>
                    <PaymentCtl disabled={quote.status !== QuoteStatus.Draft} paymentTerms={quote.paymentTerms ?? PaymentTerms.Cash.toString()}
                                payment={quote.payments[0]} onChange={this.onPaymentChange} isQuote={true}/>
                </section>

                <section className="products">
                    <LineItemsCtl salesBase={quote} childrenCustomers={quote.shippingCustomer.isStudent ? {} : this.state.childrenCustomers}
                                  updateOrderOrQuote={this.setStateQuote} showError={this.showError} 
                                  disabled={quote.status !== QuoteStatus.Draft} checkingTaxes={this.checkingTaxes}/>
                </section>

                <section className="comments">
                    <LabelCtl label="Comments"><TextCtl type="textarea" value={quote.comments} onChange={this.onCommentsChange}
                                                        disabled={quote.status !== QuoteStatus.Draft}/></LabelCtl>
                </section>

                <section className="summary">
                    <div id="LineItemTotal"><span>Subtotal</span><span>{quote.totalItemAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="DiscountTotal"><span>Discount</span><span>{quote.totalDiscountAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="FrieghtTotal"><span>Freight</span><span>{quote.freightAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="SalesTax"><span>Sales Tax</span><span>{this.state.taxInProgress && <div className="tax spinner"></div>}{quote.totalTaxAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="Total"><span>Total</span><span>{quote.totalAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                </section>
            </main>

            {this.state.inProgress !== "" &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}

            <footer className="quoteFooter">
                {/*<button className="secondary">Teachers</button>*/}
                <button className="tertiary" disabled={quote.quoteNumber === "" || quote.status !== QuoteStatus.Draft || this.state.inProgress !== "" || isAccountingPersonnel()}
                    onClick={() => this.setState({ showModal: true })}>Delete</button>
                <button className="tertiary" disabled={isAccountingPersonnel()} onClick={() => this.setState({ showCloseModal: true })}>Manually Close Quote</button>
                <button className="secondary" disabled={quote.quoteNumber === ""} onClick={() => this.setState({ showAttachmentModal: true })}>Attachments</button>
                <button className="secondary" onClick={() => this.closeTheWindow()}>Close</button>
                <button disabled={(quote.shippingCustomer?.isStudent === false && quote.shippingCustomer?.customerNumber === "") ||
                                  (quote.shippingCustomer?.isStudent === true && quote.shippingCustomer?.parentCustomerNumber === "") ||
                                  quote.status !== QuoteStatus.Draft || this.state.taxInProgress || this.state.inProgress !== "" || isAccountingPersonnel()}
                        className="primary" onClick={() => this.saveQuote()}>Save</button>
            </footer>
            </div>;
    }
}
